import Vue from '../../assets/vue';
import {
  normalizeProductConfig,
  formatPriceWithoutDecimals,
  calcNetPrice,
  calcSalePrice,
  calcDiscountAmount,
  ADD_PRODUCT_TO_CART_ERROR_MSG,
  calcBundlePreOrderMessage,
  calcBundleVariantsForSell,
  isBundleAvailable,
  findUnavailableBundleVariant,
  formatPriceWithoutZeros,
} from '../../scripts/utils/product-helpers';
import { addItems } from '../../scripts/cartApi';
import { sendCustomEvent } from '../../scripts/config/analytics';
import {
  initCart,
  openSideCart,
} from '../../templates/common/side-cart/side-cart';
import { calcBogoBonusProducts } from '../../scripts/utils/bogo-helpers';
import {
  ADD_TO_CART_TEXT,
  NOT_AVAILABLE_TEXT,
  PRE_ORDER_TEXT,
} from '../../scripts/config/constants';

const {
  products,
  variantsPreviewImage,
  variantsMeta,
  bogo,
  bundleConfig,
  warrantyConfig,
} = window.productCardsConfig;
const FREE_FRAME_OPTION_NAME = 'white';

const CreateProductTile = (productId) => ({
  data() {
    this.normalizedProductActive = normalizeProductConfig(products[productId]);
    this.normalizedVariantsList = this.normalizedProductActive.variants;
    const normalizedVariantActive = this.normalizedProductActive.variants[0];

    return {
      normalizedVariantActive,
      selectedColor: normalizedVariantActive.handleizedOption1,
      isSpinnerActive: false,
      quantity: 1,
      errorMessage: '',
      loaded: false,
    };
  },
  mounted() {
    this.loaded = true;
  },
  computed: {
    previewImage() {
      return variantsPreviewImage[this.normalizedVariantActive.id];
    },
    variantActiveMeta() {
      const { id } = this.normalizedVariantActive;

      return variantsMeta[id];
    },
    variantActiveBundleConfig() {
      return bundleConfig[this.normalizedVariantActive.id];
    },
    variantActiveWarrantyConfig() {
      return warrantyConfig?.[this.normalizedVariantActive.id];
    },
    salePrice() {
      const { discountPrice } = this;
      const { price } = this.normalizedVariantActive;

      return calcSalePrice(price, discountPrice);
    },
    discountPrice() {
      const { discountPrice } = this.variantActiveMeta;

      if (discountPrice) {
        return discountPrice;
      }

      return null;
    },
    formattedSalePrice() {
      return formatPriceWithoutZeros(this.salePrice);
    },
    netPrice() {
      const { normalizedVariantActive, discountPrice } = this;

      return calcNetPrice(
        normalizedVariantActive.price,
        normalizedVariantActive.compare_at_price,
        discountPrice
      );
    },
    formattedNetPrice() {
      return formatPriceWithoutZeros(this.netPrice);
    },
    discountAmount() {
      const { discountPrice, normalizedVariantActive } = this;

      return calcDiscountAmount(
        normalizedVariantActive.price,
        normalizedVariantActive.compare_at_price,
        discountPrice
      );
    },
    formattedDiscountAmount() {
      const { discountAmount } = this;

      if (discountAmount) {
        return formatPriceWithoutDecimals(discountAmount);
      }

      return null;
    },
    formattedWarrantyPrice() {
      const price =
        this.variantActiveWarrantyConfig?.variants.reduce(
          (acc, variant) => (variant.price ? acc + variant.price : acc),
          0
        ) || null;

      return formatPriceWithoutDecimals(price);
    },
    colorLabel() {
      return this.selectedColor.replaceAll('-', ' ');
    },
    preOrderMessage() {
      const { preOrderCollectionsText } = this.variantActiveMeta;

      return calcBundlePreOrderMessage(
        this.variantActiveBundleConfig,
        preOrderCollectionsText
      );
    },
    submitButtonText() {
      if (!this.isVariantActiveAvailable) {
        return this.variantActiveMeta.notAvailableText || NOT_AVAILABLE_TEXT;
      }

      if (this.preOrderMessage) {
        return PRE_ORDER_TEXT;
      }

      return ADD_TO_CART_TEXT;
    },
    isVariantActiveAvailable() {
      const { isVariantInStock, normalizedVariantActive, errorMessage } = this;

      const isVariantActiveInStock = isVariantInStock(normalizedVariantActive);
      const isErrorEmpty = errorMessage === '';

      return isVariantActiveInStock && isErrorEmpty;
    },
    unavailableItem() {
      return findUnavailableBundleVariant(this.variantActiveBundleConfig);
    },
    hasWarrantyProduct() {
      return Boolean(this.variantActiveWarrantyConfig);
    },
  },
  methods: {
    isVariantInStock(variant) {
      const areAllVariantsAvailable = isBundleAvailable(
        bundleConfig[variant.id]
      );

      return variant.available && areAllVariantsAvailable;
    },
    isActiveVariant(variantId) {
      return variantId === this.normalizedVariantActive.id;
    },
    changeActiveVariant(normalizedVariant) {
      this.normalizedVariantActive =
        normalizedVariant || this.normalizedProductActive.variants[0];
      this.selectedColor = this.normalizedVariantActive.handleizedOption1;
      this.errorMessage = '';
    },
    makeTooltipTextTop(normalizedVariant) {
      return `${normalizedVariant.handleizedOption1.toUpperCase()}`;
    },
    makeTooltipTextBottom(normalizedVariant) {
      if (
        normalizedVariant.available &&
        this.normalizedProductActive.available
      ) {
        const isVariantAnExtraFrame =
          FREE_FRAME_OPTION_NAME !== normalizedVariant.handleizedOption1;

        if (isVariantAnExtraFrame) {
          return `Sold Separately`;
        }

        return 'Included';
      }

      if (!this.normalizedProductActive.available) {
        return `Sold Out`;
      }
      return `Available for Pre-Order`;
    },
    calcVariantsForSale(withWarranty) {
      const config = withWarranty
        ? this.variantActiveWarrantyConfig
        : this.variantActiveBundleConfig;

      const mainProducts = calcBundleVariantsForSell(config, false);

      const bogoProducts = mainProducts
        .reduce((acc, { id }) => {
          if (bogo[id]?.is_active) {
            acc.push(...calcBogoBonusProducts(bogo[id]));
          }

          return acc;
        }, [])
        .filter(Boolean);

      if (bogoProducts?.length) {
        return [...bogoProducts, ...mainProducts];
      }

      return mainProducts;
    },
    async submitProductForm(e, withWarranty = false) {
      e.preventDefault();

      this.isSpinnerActive = true;
      await initCart();

      const items = this.calcVariantsForSale(withWarranty);

      try {
        await addItems(items);

        const { sideCart } = window;

        sideCart.updateView(null, openSideCart);

        const addedVariantsIds = items.map((item) => item.id);

        addedVariantsIds.forEach((addedVariantId) => {
          sendCustomEvent({
            category: 'Cart',
            action: 'Add',
            label: addedVariantId,
          });
        });
      } catch (err) {
        if (err.headers) {
          err.json().then((body) => {
            this.errorMessage = body.message;
          });
        } else {
          this.errorMessage = ADD_PRODUCT_TO_CART_ERROR_MSG;
        }
        console.error(err);
      }

      this.isSpinnerActive = false;
    },
    openWishListModal() {
      if (this.unavailableItem) {
        window.productWishListModal?.updateModalState({
          newVariantId: this.unavailableItem.id,
        });
      }
      window.productWishListModal?.openModal();
    },
  },
});

export const initProductBuyTile = (productId) => {
  const productTile = CreateProductTile(productId);

  if (productTile) {
    new Vue(productTile).$mount(`#product-buy-tile-control-${productId}`);
  }
};
